import React, { useState, useEffect } from "react";
import { manageAuthorApi } from "../../../entities/author";
import { Author } from "../../../entities/author";
import "./AuthorManagement.scss";

const AuthorManagement: React.FC = () => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "authorName" | "createdAt" | "numOfArticles"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [authorToDelete, setAuthorToDelete] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<Author | null>(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    authorName: "",
    authorEmail: "",
    authorInsta: "",
    authorLinkedin: "",
    numOfArticles: 0,
  });

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      const authorsData = await manageAuthorApi.getAuthorList();
      setAuthors(authorsData);
    } catch (error) {
      console.error("Failed to fetch authors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAuthor = async (id: string) => {
    try {
      await manageAuthorApi.deleteAuthor(id);
      setAuthors(authors.filter((author) => author.id !== id));
      setShowDeleteModal(false);
      setAuthorToDelete(null);
    } catch (error) {
      console.error("Failed to delete author:", error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedAuthors.map((id) => manageAuthorApi.deleteAuthor(id)),
      );
      setAuthors(
        authors.filter((author) => !selectedAuthors.includes(author.id)),
      );
      setSelectedAuthors([]);
    } catch (error) {
      console.error("Failed to delete authors:", error);
    }
  };

  const handleAddAuthor = async () => {
    try {
      await manageAuthorApi.addAuthor(formData);
      await fetchAuthors();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error("Failed to add author:", error);
    }
  };

  const handleUpdateAuthor = async () => {
    if (!editingAuthor) return;

    try {
      await manageAuthorApi.updateAuthor(editingAuthor.id, formData);
      await fetchAuthors();
      setShowEditModal(false);
      setEditingAuthor(null);
      resetForm();
    } catch (error) {
      console.error("Failed to update author:", error);
    }
  };

  const openEditModal = (author: Author) => {
    setEditingAuthor(author);
    setFormData({
      authorName: author.authorName,
      authorEmail: author.authorEmail || "",
      authorInsta: author.authorInsta || "",
      authorLinkedin: author.authorLinkedin || "",
      numOfArticles: author.numOfArticles,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      authorName: "",
      authorEmail: "",
      authorInsta: "",
      authorLinkedin: "",
      numOfArticles: 0,
    });
  };

  const filteredAuthors = authors.filter((author) => {
    const matchesSearch =
      author.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (author.authorEmail &&
        author.authorEmail.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const sortedAuthors = [...filteredAuthors].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "authorName":
        aValue = a.authorName;
        bValue = b.authorName;
        break;
      case "createdAt":
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case "numOfArticles":
        aValue = a.numOfArticles;
        bValue = b.numOfArticles;
        break;
      default:
        return 0;
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedAuthors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAuthors = sortedAuthors.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAuthors(paginatedAuthors.map((author) => author.id));
    } else {
      setSelectedAuthors([]);
    }
  };

  const handleSelectAuthor = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedAuthors([...selectedAuthors, id]);
    } else {
      setSelectedAuthors(selectedAuthors.filter((authorId) => authorId !== id));
    }
  };

  if (loading) {
    return (
      <div className="author-management">
        <div className="author-management__header">
          <h1>작성자 관리</h1>
          <p>작성자를 관리하고 편집하세요.</p>
        </div>
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="author-management">
      <div className="author-management__header">
        <h1>작성자 관리</h1>
        <p>작성자를 관리하고 편집하세요.</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="author-management__filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="작성자 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="sort-box">
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(
                e.target.value as "authorName" | "createdAt" | "numOfArticles",
              )
            }
          >
            <option value="authorName">이름순</option>
            <option value="createdAt">생성일순</option>
            <option value="numOfArticles">아티클 수순</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
          >
            <option value="asc">오름차순</option>
            <option value="desc">내림차순</option>
          </select>
        </div>
      </div>

      {/* 액션 버튼 */}
      <div className="author-management__actions">
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          작성자 추가
        </button>
        {selectedAuthors.length > 0 && (
          <button
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            선택 삭제 ({selectedAuthors.length})
          </button>
        )}
      </div>

      {/* 작성자 테이블 */}
      <div className="author-management__table">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedAuthors.length === paginatedAuthors.length &&
                    paginatedAuthors.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>작성자 이름</th>
              <th>이메일</th>
              <th>인스타그램</th>
              <th>링크드인</th>
              <th>관련 아티클 수</th>
              <th>생성일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAuthors.map((author) => (
              <tr key={author.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedAuthors.includes(author.id)}
                    onChange={(e) =>
                      handleSelectAuthor(author.id, e.target.checked)
                    }
                  />
                </td>
                <td className="author-name">{author.authorName}</td>
                <td>
                  {author.authorEmail ? (
                    <a
                      href={`mailto:${author.authorEmail}`}
                      className="email-link"
                    >
                      {author.authorEmail}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {author.authorInsta ? (
                    <a
                      href={author.authorInsta}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link insta-link"
                    >
                      @{author.authorInsta.split("/").pop()}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {author.authorLinkedin ? (
                    <a
                      href={author.authorLinkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link linkedin-link"
                    >
                      프로필 보기
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td>{author.numOfArticles}</td>
                <td>{new Date(author.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openEditModal(author)}
                  >
                    수정
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setAuthorToDelete(author.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="author-management__pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          이전
        </button>
        <span>
          {currentPage} / {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          다음
        </button>
      </div>

      {/* 작성자 추가 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>작성자 추가</h3>
              <button onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>작성자 이름 *</label>
                <input
                  type="text"
                  value={formData.authorName}
                  onChange={(e) =>
                    setFormData({ ...formData, authorName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>이메일</label>
                <input
                  type="email"
                  value={formData.authorEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, authorEmail: e.target.value })
                  }
                  placeholder="author@example.com"
                />
              </div>
              <div className="form-group">
                <label>인스타그램</label>
                <input
                  type="url"
                  value={formData.authorInsta}
                  onChange={(e) =>
                    setFormData({ ...formData, authorInsta: e.target.value })
                  }
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div className="form-group">
                <label>링크드인</label>
                <input
                  type="url"
                  value={formData.authorLinkedin}
                  onChange={(e) =>
                    setFormData({ ...formData, authorLinkedin: e.target.value })
                  }
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div className="form-group">
                <label>관련 아티클 수</label>
                <input
                  type="number"
                  value={formData.numOfArticles}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numOfArticles: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddAuthor}
                disabled={!formData.authorName}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 작성자 수정 모달 */}
      {showEditModal && editingAuthor && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>작성자 수정</h3>
              <button onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>작성자 이름 *</label>
                <input
                  type="text"
                  value={formData.authorName}
                  onChange={(e) =>
                    setFormData({ ...formData, authorName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>이메일</label>
                <input
                  type="email"
                  value={formData.authorEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, authorEmail: e.target.value })
                  }
                  placeholder="author@example.com"
                />
              </div>
              <div className="form-group">
                <label>인스타그램</label>
                <input
                  type="url"
                  value={formData.authorInsta}
                  onChange={(e) =>
                    setFormData({ ...formData, authorInsta: e.target.value })
                  }
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div className="form-group">
                <label>링크드인</label>
                <input
                  type="url"
                  value={formData.authorLinkedin}
                  onChange={(e) =>
                    setFormData({ ...formData, authorLinkedin: e.target.value })
                  }
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div className="form-group">
                <label>관련 아티클 수</label>
                <input
                  type="number"
                  value={formData.numOfArticles}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numOfArticles: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateAuthor}
                disabled={!formData.authorName}
              >
                수정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>삭제 확인</h3>
              <button onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {authorToDelete ? (
                <p>선택한 작성자를 삭제하시겠습니까?</p>
              ) : (
                <p>
                  선택한 {selectedAuthors.length}명의 작성자를 삭제하시겠습니까?
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (authorToDelete) {
                    handleDeleteAuthor(authorToDelete);
                  } else {
                    handleBulkDelete();
                  }
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorManagement;
